import { useEffect, useRef, useState, useCallback } from "react"
import { Loader2, Plus } from "lucide-react"

import If from "../if"
import { LightBtn, PrimaryBtn } from "../buttons"
import { REGEX_HTPPS_URL_WILDCARD_QUERY } from "../../constants"

const INVALID_CLASSNAME = "!ring-red-500 !ring-1"
/**
 * Regular expression that matches the below URL patterns
 *  - https://www.example.com
 *  - https:/*.example.com
 *  - https://example.com/*
 *  - https://*.example.com/*
 */

type Props = {
  url: string
  resetOnBlur?: boolean
  onChange: (url: string) => void
  onSubmit: () => void
  onBlur?: () => void
  onCancel?: () => void
  isLoading?: boolean
  id?: string
  resetInputOnSubmit?: boolean
}

const InputURL = ({
  url,
  onChange,
  onSubmit,
  resetOnBlur,
  onBlur,
  onCancel,
  isLoading = false,
  resetInputOnSubmit,
  id
}: Props) => {
  const newURLInput = useRef<HTMLInputElement>(null)
  const [isInvalidInput, setIsInvalidInput] = useState<boolean>(
    url.length > 0 && !REGEX_HTPPS_URL_WILDCARD_QUERY.test(url)
  )

  useEffect(() => {
    newURLInput.current?.focus()
  }, [])

  const blurInput = useCallback(() => {
    setIsInvalidInput(false)
    // blur newURLInput
    newURLInput.current?.blur?.()
  }, [])

  const resetInput = useCallback(() => {
    if (resetInputOnSubmit) onChange("")
    blurInput()
  }, [resetInputOnSubmit, onChange, blurInput])

  const handleOnChange = useCallback(
    (value: string) => {
      onChange(value)
      setIsInvalidInput(!REGEX_HTPPS_URL_WILDCARD_QUERY.test(value))
    },
    [onChange]
  )

  const handleOnBlur = useCallback(() => {
    if (resetOnBlur) {
      resetInput()
      onBlur?.()
    }
  }, [resetOnBlur, resetInput, onBlur])

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter") {
        if (REGEX_HTPPS_URL_WILDCARD_QUERY.test(url)) {
          blurInput()
          onSubmit()
        } else {
          setIsInvalidInput(true)
        }
      } else if (e.key === "Escape") {
        e.stopPropagation()
        if (onCancel) {
          onCancel()
        } else {
          resetInput()
        }
      }
    },
    [url, blurInput, onSubmit, onCancel, resetInput]
  )

  return (
    <div className="flex relative w-full">
      <If
        condition={url.length === 0}
        then={
          <div className="pointer-events-none absolute inset-y-0 left-2 flex items-center">
            <span className="text-neutral-400">
              <Plus size={20} />
            </span>
          </div>
        }
      />
      <input
        ref={newURLInput}
        id={id}
        type="url"
        value={url}
        onBlur={handleOnBlur}
        onKeyDown={handleKeyDown}
        onChange={e => handleOnChange(e.target.value)}
        className={`focus:ring-0 text-base-dark block border-0 w-full h-12 sm:text-sm rounded-sm peer placeholder:text-neutral-400 ${
          isInvalidInput ? INVALID_CLASSNAME : ""
        } ${url.length ? "pl-2" : "pl-7"}`}
        placeholder="Add new URL"
      />
      <If
        condition={isLoading}
        then={
          <div className="pointer-events-none absolute right-0 items-center pr-3 top-1.5">
            <Loader2
              className="animate-spin h-6 w-6 text-primary-alt-400"
              aria-hidden="true"
            />
          </div>
        }
      />
      <If
        condition={!isLoading && url.length > 0}
        then={
          <div className="flex items-center ml-4">
            <LightBtn onClick={onCancel ?? resetInput} className="mr-2">
              Cancel
            </LightBtn>
            <PrimaryBtn
              onClick={onSubmit}
              disabled={url.length === 0 || isInvalidInput}
            >
              Save
            </PrimaryBtn>
          </div>
        }
      />
    </div>
  )
}

export default InputURL
